import { Depot, DepotType } from "@/api.types";
import { WarehouseIcon } from "lucide-react";
import {
  getMarkerSizesByZoom,
  shouldShowTooltip,
  shouldShowIcon,
  shouldShowStatus,
} from "@/components/markers/marker.util";

interface DepotMarkerProps {
  depot: Depot;
  onClick?: () => void;
  zoomLevel: number;
}

export function DepotMarker({ depot, onClick, zoomLevel }: DepotMarkerProps) {
  const isActive = depot.status === "ACTIVE";
  const sizes = getMarkerSizesByZoom(zoomLevel);

  const getDepotTypeColor = (type: DepotType) => {
    switch (type) {
      case DepotType.HUB:
        return "bg-purple-500";
      case DepotType.WAREHOUSE:
        return "bg-blue-500";
      case DepotType.MICRO_HUB:
        return "bg-green-500";
      default:
        return "bg-gray-500";
    }
  };

  const getDepotIcon = () => {
    return <WarehouseIcon size={sizes.icon} color="white" />;
  };

  return (
    <button
      type="button"
      className={`relative group cursor-pointer transform hover:scale-110 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full ${
        !isActive ? "opacity-75" : ""
      }`}
      onClick={onClick}
      aria-label={`${depot.name} - ${depot.type} - ${depot.status}`}
    >
      <div
        role="img"
        aria-label={depot.name}
        className={`${sizes.container} rounded-lg flex items-center justify-center text-white ${sizes.fontSize} font-medium ${sizes.border} ${getDepotTypeColor(
          depot.type
        )} border-blue-500 transition-all duration-200`}
      >
        {shouldShowIcon(zoomLevel) && getDepotIcon()}
      </div>

      {shouldShowStatus(zoomLevel) && (
        <div
          role="status"
          aria-label={`Status: ${depot.status}`}
          className={`absolute -bottom-1 -right-1 ${sizes.statusIndicator} rounded-full ${sizes.border} border-white ${
            depot.status === "ACTIVE" ? "bg-green-500" : "bg-gray-600"
          }`}
        />
      )}

      {shouldShowTooltip(zoomLevel) && (
        <div
          role="tooltip"
          className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 hidden group-hover:block bg-white px-3 py-2 rounded shadow-lg text-sm whitespace-nowrap z-10"
        >
          <div className="font-medium">{depot.name}</div>
          <div className="text-xs text-gray-500">
            {depot.type.replace("_", " ")}
          </div>
          <div className="text-xs text-gray-500">
            Depot #{depot.depotNumber}
          </div>
        </div>
      )}
    </button>
  );
}
